import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { Form, Formik } from "formik";
import OnlineReservationSteps from "../../OnlineReservationSteps";
import FormSelectBox from "../../../../components/Forms/FormSelectBox";
import ReservationChildrenFields from "../../../../components/Forms/ReservationChildrenFields";
import useOnlineReservation from "../../hooks/useOnlineReservation";
import { showErrorToast } from "../../../../components/Toasts";
import * as Yup from "yup";
import { dateFormatter } from "../../../../global";
import { Form as BTForm } from "react-bootstrap";
import { Link } from "react-router-dom";
import OnlineReservationPriceTable from "../../OnlineReservationPriceTable";
import OnlineReservationDateRangePicker from "../../OnlineReservationDateRangePicker";
import { OnlineReservationContext } from "../../index";
import { navigateTo } from "../../../../global/navigationHelper";
import { useTranslation } from "react-i18next";
import ParkingSlotPreview from "src/components/ParkingSlotPreview";
import { iconsEnum } from "../../../../components/UiComponents/IconSelect";
import axios from "../../../../config/axios";
import classNames from "classnames";

const EditOnlineReservationBookingDetailsPage = () => {
  const { update, onlineReservation } = useOnlineReservation();
  const [configuration, setConfiguration] = useState();
  const [parkingSlotsLoading, setParkingSlotsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState();
  const { t } = useTranslation("reservations");
  const { business, configuration: reservationConfiguration } = useContext(
    OnlineReservationContext
  );

  const [showDateRange, setShowDateRange] = useState(false);

  function getConfiguration(options) {
    let query = `/online_reservations/${onlineReservation.token}/available_parking_slots.json`;

    if (options?.parking_slot_category_id) {
      query += `?parking_slot_category_id=${options.parking_slot_category_id}`;
    }

    setParkingSlotsLoading(true);

    axios
      .get(query)
      .then(function (response) {
        setConfiguration(response.data.data);
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(() => {
        setParkingSlotsLoading(false);
      });
  }

  useEffect(() => {
    if (onlineReservation) {
      getConfiguration();
    }
  }, [onlineReservation?.token]);

  async function handleSubmit(values) {
    try {
      const onlineReservation = await update(values);
      navigateTo(`/reservations/${onlineReservation.token}/contact-details`);
    } catch (e) {
      console.error(e);
      showErrorToast(e.message);
    }
  }

  async function saveChildAges(ages) {
    if (ages.includes("")) return;

    try {
      await update({
        token: onlineReservation.token,
        reservation_child_ages: ages,
      });
    } catch (error) {
      showErrorToast(error.message);
    }
  }

  if (!onlineReservation || !configuration) {
    return <></>;
  }

  function checkFormValidation(isValid) {
    if (!isValid) {
      showErrorToast(t("common:general.errors.required_fields"));
    }
  }

  return (
    <>
      <OnlineReservationSteps step={1} />
      <Formik
        initialValues={{ ...onlineReservation }}
        onSubmit={handleSubmit}
        validationSchema={() => {
          return Yup.object({
            parking_slot_id: Yup.number().required(
              t("errors:errors.general.required")
            ),
            amount_guests: Yup.number()
              .min(1, t("errors:errors.general.required"))
              .required(),
            reservation_child_ages: Yup.array().of(
              Yup.number().required(t("errors:errors.general.required"))
            ),
            amount_dogs: Yup.number().required(),
          });
        }}
      >
        {({ values, setFieldValue, errors, isValid }) => {
          return (
            <Form autoComplete="false">
              <Row>
                <Col>
                  <h2>{t("editBookingDetails.title")}</h2>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={9}>
                  <BTForm.Group className="mb-3">
                    <BTForm.Label>{t("editBookingDetails.stay")}</BTForm.Label>
                    <div
                      onClick={() => {
                        setShowDateRange(!showDateRange);
                      }}
                    >
                      <BTForm.Control
                        value={`${dateFormatter(
                          onlineReservation.from
                        )} - ${dateFormatter(onlineReservation.to)}`}
                        onChange={() => {}}
                      />
                    </div>
                  </BTForm.Group>

                  {showDateRange && (
                    <OnlineReservationDateRangePicker
                      from={values.from}
                      to={values.to}
                      setFieldValue={setFieldValue}
                      reservationYears={reservationConfiguration?.reservation_years}
                      onChange={async (from, to) => {
                        if (from < to) {
                          setFieldValue("from", from);
                          setFieldValue("to", to);
                          await update({
                            token: onlineReservation.token,
                            from: from,
                            to: to,
                          });
                          getConfiguration();
                          setSelectedCategory(null);
                        }
                      }}
                    />
                  )}
                  <Row>
                    <Col xs={12} sm={6}>
                      <FormSelectBox
                        name="amount_guests"
                        label={t("editBookingDetails.amountAdults")}
                        required={true}
                        options={Array.from({ length: 6 }, (_, index) => ({
                          value: index + 1,
                          name: t("common:online_reservation.amountAdults", {
                            count: index + 1,
                          }),
                        }))}
                        onChange={(e) => {
                          setFieldValue(
                            "amount_guests",
                            Number(e.target.value) || 0
                          );
                          update({
                            token: onlineReservation.token,
                            amount_guests: e.target.value || 0,
                          });
                        }}
                      ></FormSelectBox>
                    </Col>
                  </Row>
                  <ReservationChildrenFields onAgesChange={saveChildAges} />
                  {business?.settings?.dogs_enabled && (
                    <Row>
                      <Col>
                        <FormSelectBox
                          name="amount_dogs"
                          label={t("editBookingDetails.amountDogs")}
                          required={true}
                          options={Array.from({ length: 5 }, (_, index) => ({
                            value: index,
                            name: t("common:online_reservation.amountDogs", {
                              count: index,
                            }),
                          }))}
                          onChange={(e) => {
                            setFieldValue(
                              "amount_dogs",
                              Number(e.target.value) || 0
                            );
                            update({
                              token: onlineReservation.token,
                              amount_dogs: Number(e.target.value) || 0,
                            });
                          }}
                        ></FormSelectBox>
                      </Col>
                    </Row>
                  )}
                  {onlineReservation.parking_slot && (
                    <Row>
                      <Col xs={12} md={6}>
                        <ParkingSlotPreview
                          className="h-100 mb-3"
                          parkingSlot={onlineReservation.parking_slot}
                          colors={configuration.parking_slot_category_colors}
                          selected={true}
                        />

                        <div className={"d-flex justify-content-end my-3"}>
                          <Button
                            type={"submit"}
                            onClick={() => checkFormValidation(isValid)}
                          >
                            {t("common:components.button.next")}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Row className={"align-items-end"}>
                    <Col className={"mb-3"}>
                      {t("editBookingDetails.selectParkingSlot")} {"("}
                      <Link
                        target={"_blank"}
                        to={{ pathname: business?.camp_site_map?.url }}
                      >
                        {t("editBookingDetails.downloadCampsite")}
                      </Link>
                      {")"}
                    </Col>
                  </Row>

                  <div className="d-flex align-items-center mb-3">
                    <div
                      className={classNames(
                        "mr-2 align-self-end cursor-pointer",
                        { "font-weight-bold": !selectedCategory }
                      )}
                      onClick={() => {
                        getConfiguration();
                        setSelectedCategory(null);
                      }}
                    >
                      {t("common:general.all")}
                    </div>

                    {configuration.parking_slot_categories.map(
                      (category, index, arr) => (
                        <div
                          key={category.id}
                          className="d-flex align-items-center cursor-pointer"
                          onClick={() => {
                            getConfiguration({
                              parking_slot_category_id: category.id,
                            });
                            setSelectedCategory(category);
                          }}
                        >
                          <div className="text-center mr-2">
                            <i
                              className={`campicon-${
                                iconsEnum[category?.icon]
                              } h4 pb-0 mb-0 mt-1`}
                              style={{
                                color: category?.color?.value ?? "#fff",
                              }}
                            ></i>
                            <div
                              className={classNames({
                                "font-weight-bold":
                                  selectedCategory?.id === category.id,
                              })}
                            >
                              {category.name}
                            </div>
                          </div>
                          {index < arr.length - 1 && (
                            <div
                              style={{
                                width: "1px",
                                height: "2rem",
                                backgroundColor: "#ccc",
                                margin: "0 0.5rem",
                              }}
                            ></div>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {configuration.available_parking_slots.length === 0 && (
                    <div>{t("editBookingDetails.noAvailableParkingSlots")}</div>
                  )}

                  <Row>
                    {parkingSlotsLoading ? (
                      <Spinner
                        className={"d-block mx-auto"}
                        animation="border"
                        variant="primary"
                      />
                    ) : (
                      configuration.available_parking_slots.map(
                        (parking_slot) => (
                          <Col
                            key={parking_slot.id}
                            xs={12}
                            md={6}
                            className={"mb-3"}
                          >
                            <ParkingSlotPreview
                              className="h-100"
                              parkingSlot={parking_slot}
                              colors={
                                configuration.parking_slot_category_colors
                              }
                              selected={
                                parking_slot.id === values.parking_slot_id
                              }
                              onClick={() => {
                                setFieldValue(
                                  "parking_slot_id",
                                  parking_slot.id
                                );
                                update({
                                  token: onlineReservation.token,
                                  parking_slot_id: parking_slot.id,
                                });
                              }}
                            />

                            {parking_slot.id === values.parking_slot_id && (
                              <div
                                className={"d-flex justify-content-end mt-3"}
                              >
                                <Button
                                  type={"submit"}
                                  onClick={() => checkFormValidation(isValid)}
                                >
                                  {t("common:components.button.next")}
                                </Button>
                              </div>
                            )}
                          </Col>
                        )
                      )
                    )}
                  </Row>

                  {errors.parking_slot_id && (
                    <span className="text-danger">
                      {t("errors:errors.general.required")}
                    </span>
                  )}
                </Col>

                <Col md={3}>
                  <OnlineReservationPriceTable
                    onlineReservation={onlineReservation}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={9}>
                  <div className={"d-flex justify-content-end mb-3"}>
                    <Button type={"submit"}>
                      {t("common:components.button.next")}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EditOnlineReservationBookingDetailsPage;
